import "app/App.css";

import { Route, Switch } from "react-router-dom";
import RobotDetails from "features/robot-teleop/pages/SettingPage/robot-details";
import MCAPPlayer from "features/mcap-player";
import { getConnect2RobotWsURL } from "shared/config/setting";
import PageRobotCamera from "features/robot-teleop/pages/MonitorPage/robot-camera-view";
import { Login } from "features/auth/pages/LoginPage/Login";
import { RequiresAuth } from "shared/components/common/RequiresAuth";
import { ServerConnectionProvider } from "shared/contexts/ServerConnectionContext";
import SpotTeleControlView_NEW from "features/robot-teleop/pages/RobotPage/spot-tele-control-view-new";
import { UserProfileEditPage } from "features/auth/pages/ProfilePage/UserProfileEdit";
import UserRobotsDashBoard from "features/dashboard/pages/HomePage/robots-dashboard-user";
import { UserProvider } from "shared/contexts/UserDataProvider";
import { UserDataContextProvider } from "shared/contexts/UserDataProviderNew";
import { RobotPeerConnectionProvider } from "features/robot-teleop/contexts/RobotConnectionContext";
import { Signup } from "features/auth/pages/SignupPage/Signup";
import Main from "shared/components/layout/Main";
import RobotGroupControlPage from "features/robot-teleop/pages/ControlPage/robot-group-control-page";
import RobotGroupMonitorPage from "features/robot-teleop/pages/MonitorPage/robot-group-monitor-page";
import RobotMonitorWall from "features/robot-teleop/pages/MonitorPage/robot-monitor-wall";
import { MediaManagerProvider } from "features/robot-teleop/contexts/MediaManagerContext";
import AdminApp from "features/administration/pages/AdminApp";
import OrgsManagement from "features/administration/pages/OrgsManagement";
import { OrganizationsDataProvider } from "features/administration/contexts/OrgsDataProvider";
import { UserResetPasswordPage } from "features/auth/pages/LoginPage/UserResetPassword";
import RobotsAministrator from "features/administration/pages/RobotsAdministrator";
import RobotTypesManagement from "features/administration/pages/RobotTypesManagement";
import UsersManagement from "features/administration/pages/UsersManagement";
import MyOrganizationsPage from "features/administration/pages/MyOrganizations";
import OrganizationPage from "features/administration/pages/OrganizationPage";
import MyRobotsPage from "features/administration/pages/MyRobots";
import RobotGroupControlPageNew from "features/robot-teleop/pages/ControlPage/unused/robot-group-control-page-new";
import DemobotControlPage from "features/robot-teleop/pages/ControlPage/demobot-control-page";
import { RobotConnectionsManagerProvider } from "features/robot-teleop/contexts/unused/RobotConnectionManagerProvider";
import { EventBusProvider } from "shared/services/eventBus";

// Ant Design locales
import { ConfigProvider } from "antd";
import enUS from "antd/locale/en_US";
import koKR from "antd/locale/ko_KR";
import viVN from "antd/locale/vi_VN";
import { useTranslation } from "react-i18next";
import { useAppVersionCheck } from "shared/hooks/useAppVersionCheck";
import RobotsDashboard from "features/dashboard/pages/HomePage/dashboard";
import { RaasRobotLogsPage, UploadRobotLog, McapLogsDashboard } from "features/robot-logs";
import {
  ROBOT_LOGS_DASHBOARD_PATH,
  ROBOT_LOGS_LIST_PATH,
  ROBOT_LOGS_REQUIRED_ORG_ROLES,
} from "features/robot-logs/utils/logConstants";
import McapViewer from "app/MCAPPlayerApp";
import JoyControlMappingsManagement from "features/administration/pages/JoyControlMappingsManagement";
import RobotControlPage from "features/robot-teleop/pages/ControlPage/robot-control-page";

// Helper function to get Ant Design locale based on current language
const getAntdLocale = (language) => {
  switch (language) {
    case "ko":
      return koKR;
    case "vn":
      return viVN;
    default:
      return enUS;
  }
};

function App() {
  const { i18n, ready } = useTranslation();
  useAppVersionCheck();

  const searchParams = new URLSearchParams(window.location.search);
  const qsHideMenuBar =
    searchParams.has("hide-menu-bar") &&
    searchParams.get("hide-menu-bar").trim().toLocaleLowerCase() == "true";

  const qsStatsEnabled = searchParams.has("show-stats")
    ? searchParams.get("show-stats").trim().toLocaleLowerCase() == "true"
    : null;
  const qsLocalMicAllowed = searchParams.has("local-mic")
    ? searchParams.get("local-mic").trim().toLocaleLowerCase() == "true"
    : null;
  const qsRemoteMicAllowed = searchParams.has("remote-mic")
    ? searchParams.get("remote-mic").trim().toLocaleLowerCase() == "true"
    : null;
  const qsRemoteCameraAllowed = searchParams.has("remote-cam")
    ? searchParams.get("remote-cam").trim().toLocaleLowerCase() == "true"
    : null;
  const qsTrickleICE = searchParams.has("trickle-ice")
    ? searchParams.get("trickle-ice").trim().toLocaleLowerCase() == "true"
    : null;

  return (
    <ConfigProvider locale={getAntdLocale(ready ? i18n.language : "en")}>
      <EventBusProvider options={{ enableDebug: process.env.NODE_ENV === 'development' }}>
        <Switch>
        <Route path="/admin/*" exact>
          <AdminApp />
        </Route>

        <Main>
          <Route path="/login" exact>
            <Login />
          </Route>
          <Route path="/signup" exact>
            <Signup />
          </Route>
          <Route path="/forgot-password" exact>
            <UserResetPasswordPage />
          </Route>
          <Route path="/my/profile" exact>
            <RequiresAuth>
              <UserProfileEditPage />
            </RequiresAuth>
          </Route>

          <Route path="/my/orgs" exact>
            <RequiresAuth>
              <OrganizationsDataProvider>
                <MyOrganizationsPage />
              </OrganizationsDataProvider>
            </RequiresAuth>
          </Route>
          <Route path="/my/robots" exact>
            <RequiresAuth>
              <MyRobotsPage />
            </RequiresAuth>
          </Route>
          <Route path="/organization" exact>
            <RequiresAuth>
              <OrganizationPage />
            </RequiresAuth>
          </Route>

          <Route path="/mcap-player" exact>
            <RequiresAuth>
              <McapViewer></McapViewer>
            </RequiresAuth>
          </Route>

          <Route path="/mcap-viewer" exact>
            <RequiresAuth>
              <MCAPPlayer />
            </RequiresAuth>
          </Route>

          <Route path={ROBOT_LOGS_DASHBOARD_PATH} exact>
            <RequiresAuth requiredOrgRoles={ROBOT_LOGS_REQUIRED_ORG_ROLES}>
              <McapLogsDashboard />
            </RequiresAuth>
          </Route>

          <Route path={ROBOT_LOGS_LIST_PATH} exact>
            <RequiresAuth requiredOrgRoles={ROBOT_LOGS_REQUIRED_ORG_ROLES}>
              <RaasRobotLogsPage />
            </RequiresAuth>
          </Route>

          <Route path="/robot-logs/upload" exact>
            <RequiresAuth requiredOrgRoles={ROBOT_LOGS_REQUIRED_ORG_ROLES}>
              <UploadRobotLog />
            </RequiresAuth>
          </Route>

          <Route exact path="/administrations/robots">
            <RequiresAuth>
              <RobotsAministrator />
            </RequiresAuth>
          </Route>
          <Route exact path="/administrations/robot-types">
            <RequiresAuth>
              <RobotTypesManagement />
            </RequiresAuth>
          </Route>

          <Route exact path="/administrations/robot-joy-control-configs">
            <RequiresAuth>
              <JoyControlMappingsManagement />
            </RequiresAuth>
          </Route>

          <Route exact path="/administrations/users">
            <RequiresAuth>
              <UsersManagement />
            </RequiresAuth>
          </Route>
          <Route exact path="/administrations/orgs">
            <RequiresAuth>
              <OrganizationsDataProvider>
                <OrgsManagement />
              </OrganizationsDataProvider>
            </RequiresAuth>
          </Route>

          <ServerConnectionProvider wsUrl={getConnect2RobotWsURL()}>
            <MediaManagerProvider>              
              <Route path="/remote-monitor/:id">
                <RequiresAuth requiredRoles={["operator"]}>
                  <UserProvider>
                    <RobotGroupMonitorPage></RobotGroupMonitorPage>
                  </UserProvider>
                </RequiresAuth>
              </Route>

              <Route exact path="/monitor-wall">
                <RequiresAuth requiredRoles={["operator"]}>
                  <UserProvider>
                    <RobotMonitorWall></RobotMonitorWall>
                  </UserProvider>
                </RequiresAuth>
              </Route>

              <Route path="/remote-control-old/:id">
                <RequiresAuth requiredRoles={["operator"]}>
                  <UserProvider>
                    <RobotPeerConnectionProvider
                      StatsEnabled={qsStatsEnabled ?? true}
                      allowLocalAudio={qsLocalMicAllowed ?? true}
                      allowRemoteAudio={qsRemoteMicAllowed ?? true}
                      isReceiveAudio={qsRemoteMicAllowed ?? true}
                      isSendingAudio={qsLocalMicAllowed ?? true}
                      trickleICE={qsTrickleICE ?? true}
                      disableChannelMessageState={true}
                      dataChannels={["chat"]}
                    >
                      <RobotGroupControlPage></RobotGroupControlPage>
                    </RobotPeerConnectionProvider>
                  </UserProvider>
                </RequiresAuth>
              </Route>

              <Route path="/remote-control/:robotId">
                <RequiresAuth requiredRoles={["operator"]}>                  
                      <RobotControlPage></RobotControlPage>
                </RequiresAuth>
              </Route>

              <Route path="/robots/:id">
                <RobotDetails></RobotDetails>
              </Route>
              <Route path="/robot-camera/:id">
                <RobotPeerConnectionProvider
                  StatsEnabled={qsStatsEnabled ?? true}
                  allowLocalAudio={qsLocalMicAllowed ?? true}
                  allowRemoteAudio={qsRemoteMicAllowed ?? true}
                  isReceiveAudio={qsRemoteMicAllowed ?? true}
                  isSendingAudio={qsLocalMicAllowed ?? true}
                  trickleICE={qsTrickleICE ?? true}
                  disableChannelMessageState={true}
                  dataChannels={["chat"]}
                >
                  <PageRobotCamera></PageRobotCamera>
                </RobotPeerConnectionProvider>
              </Route>
            </MediaManagerProvider>
            <Route path="/dashboard-old" exact>
              <RequiresAuth>
                <UserProvider>
                  <UserRobotsDashBoard></UserRobotsDashBoard>
                </UserProvider>
              </RequiresAuth>
            </Route>
            <Route path="/dashboard" exact>
              <RequiresAuth>
                <UserDataContextProvider>
                  <RobotsDashboard></RobotsDashboard>
                </UserDataContextProvider>
              </RequiresAuth>
            </Route>
            <Route path="/" exact>
              <RequiresAuth>
                <UserDataContextProvider>
                  <RobotsDashboard></RobotsDashboard>
                </UserDataContextProvider>
              </RequiresAuth>
            </Route>

            {/* <Redirect from="*" to="/dashboard" /> */}
          </ServerConnectionProvider>
        </Main>
      </Switch>
      </EventBusProvider>
    </ConfigProvider>
  );
}

export default App;
